import {
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Input,
  Modal,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { LogActivityDrawer } from './LogActivityDrawer';
import { createQuotation } from '../sales/api';
import { changeStage, composeWhatsApp, getLead, type ActivityType } from './api';
import { SOURCE_LABEL, STAGE_LABEL, nextStage, stageTone } from './stage';

const { Text } = Typography;

/**
 * One tap each, because this screen is used standing up between customers.
 * WhatsApp opens the deep link AND the log drawer: NKS has no Business API, so
 * the advisor sends from their own phone and the system records that they did
 * (CLAUDE.md §13e).
 */
const QUICK_ACTIONS: { type: ActivityType; icon: ReactNode }[] = [
  { type: 'CALL', icon: <PhoneOutlined /> },
  { type: 'WHATSAPP', icon: <WhatsAppOutlined /> },
  { type: 'EMAIL', icon: <MailOutlined /> },
  { type: 'VISIT', icon: <ShopOutlined /> },
];

export function LeadDetailPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();
  const [logging, setLogging] = useState<ActivityType | null>(null);
  // A ref, not state: Modal.confirm keeps the onOk it was opened with.
  const lostReason = useRef('');

  const { data, isLoading } = useQuery({ queryKey: ['lead', uid], queryFn: () => getLead(uid) });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['lead', uid] });
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  /**
   * Quote straight from the lead. The API carries the name and phone across and
   * creates the customer if there is not one yet — the advisor retypes nothing,
   * which is the whole point of CLAUDE.md §2's "no re-key downstream".
   */
  const quote = useMutation({
    mutationFn: () => createQuotation({ leadUid: uid }),
    onSuccess: (created) => {
      void message.success(t('sales.created'));
      void navigate(`/quotations/${created.uid}?choose=bike`);
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('sales.createFailed'));
    },
  });

  /**
   * WhatsApp is different from the other quick actions: the API composes the
   * message, logs the activity, and hands back a link. The advisor's only job
   * is to press send in WhatsApp — nothing to type, nothing to remember to log.
   */
  const whatsapp = useMutation({
    mutationFn: () => composeWhatsApp(uid, 'lead.first_contact'),
    onSuccess: (result) => {
      window.open(result.link, '_blank', 'noopener');
      refresh();
      void message.success(t('crm.waOpened'));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('crm.waFailed'));
    },
  });

  const advance = useMutation({
    mutationFn: (input: Parameters<typeof changeStage>[1]) => changeStage(uid, input),
    onSuccess: () => {
      refresh();
      void message.success(t('crm.stageMoved'));
    },
    onError: (e: unknown) => {
      // A backward move without crm.reopen_stage is the common case; say what to
      // do rather than showing a bare 403.
      void message.error(
        e instanceof ApiError && e.code === 'PERMISSION_DENIED'
          ? t('crm.needManager')
          : e instanceof ApiError
            ? e.message
            : t('crm.stageFailed'),
      );
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  const forward = nextStage(data.stage);
  const isClosed = data.stage === 'LOST' || data.stage === 'DELIVERED';

  const confirmLost = (): void => {
    lostReason.current = '';
    Modal.confirm({
      title: t('crm.markLost'),
      content: (
        <Flex vertical gap={8}>
          <Text type="secondary">{t('crm.lostHelp')}</Text>
          <Input
            placeholder={t('crm.lostPlaceholder')}
            onChange={(e) => {
              lostReason.current = e.target.value;
            }}
          />
        </Flex>
      ),
      okText: t('crm.markLost'),
      okButtonProps: { danger: true },
      onOk: () => {
        const reason = lostReason.current.trim();
        if (reason.length === 0) {
          void message.error(t('crm.lostNeedsReason'));
          return Promise.reject(new Error('reason required'));
        }
        return advance.mutateAsync({ stage: 'LOST', lostReason: reason });
      },
    });
  };

  return (
    <>
      <DetailPage
        title={data.name}
        subtitle={data.leadNo}
        tags={[
          { label: STAGE_LABEL[data.stage], colour: stageTone(data.stage) },
          { label: SOURCE_LABEL[data.source] ?? data.source },
          ...(data.isOverdue ? [{ label: t('crm.overdue'), colour: 'error' }] : []),
        ]}
        onBack={() => {
          void navigate('/leads');
        }}
        actions={
          isClosed ? undefined : (
            <Space wrap>
              <Button
                icon={<FileTextOutlined />}
                loading={quote.isPending}
                onClick={() => {
                  quote.mutate();
                }}
              >
                {t('sales.quoteThisLead')}
              </Button>
              {forward !== null && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={advance.isPending}
                  onClick={() => {
                    advance.mutate({ stage: forward });
                  }}
                >
                  {t('crm.moveTo', { stage: STAGE_LABEL[forward] })}
                </Button>
              )}
              <Button danger icon={<CloseOutlined />} onClick={confirmLost}>
                {t('crm.markLost')}
              </Button>
            </Space>
          )
        }
        tabs={[
          {
            key: 'overview',
            label: t('crm.tabs.overview'),
            content: (
              <Flex vertical gap={16}>
                {data.firstResponseAt === null && !isClosed && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('crm.noResponseYet')}
                    description={t('crm.noResponseHelp')}
                  />
                )}

                <Card size="small" title={t('crm.logSomething')}>
                  <Flex wrap gap={8}>
                    {QUICK_ACTIONS.map((action) => (
                      <Button
                        key={action.type}
                        size="large"
                        icon={action.icon}
                        loading={action.type === 'WHATSAPP' && whatsapp.isPending}
                        onClick={() => {
                          if (action.type === 'WHATSAPP') {
                            whatsapp.mutate();
                            return;
                          }
                          setLogging(action.type);
                        }}
                      >
                        {t(`crm.activity.${action.type}`)}
                      </Button>
                    ))}
                  </Flex>
                </Card>

                <Card>
                  <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                    <Descriptions.Item label={t('fields.phone')}>{data.phone}</Descriptions.Item>
                    <Descriptions.Item label={t('fields.email')}>{orDash(data.email)}</Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.model')}>
                      {orDash(data.interestModel)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.campaign')}>
                      {orDash(data.campaign)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.advisor')}>
                      {orDash(data.assignedAdvisor)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.nextAction')}>
                      <Text type={data.isOverdue ? 'danger' : undefined}>
                        {data.nextActionAt === null ? '—' : formatDateTime(data.nextActionAt)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.firstResponse')}>
                      {data.firstResponseAt === null ? (
                        <Tag>{t('crm.notYet')}</Tag>
                      ) : (
                        <Space>
                          {formatDateTime(data.firstResponseAt)}
                          <Tag color={data.respondedWithinTarget === true ? 'success' : 'warning'}>
                            {data.respondedWithinTarget === true
                              ? t('crm.withinTarget')
                              : t('crm.missedTarget')}
                          </Tag>
                        </Space>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('crm.fields.captured')}>
                      {formatDateTime(data.createdAt)}
                    </Descriptions.Item>
                    {data.stage === 'LOST' && (
                      <Descriptions.Item label={t('crm.fields.lostReason')} span={2}>
                        {orDash(data.lostReason)}
                        {data.lostCompetitor !== null && ` — ${data.lostCompetitor}`}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Flex>
            ),
          },
          {
            key: 'quotation',
            label: t('crm.tabs.quotation'),
            disabled: true,
            content: <Card>{t('customers.comingIn', { phase: 1 })}</Card>,
          },
        ]}
      />

      {logging !== null && (
        <LogActivityDrawer
          leadUid={uid}
          type={logging}
          open
          onClose={() => {
            setLogging(null);
          }}
          onLogged={() => {
            setLogging(null);
            refresh();
            void message.success(t('crm.activitySaved'));
          }}
        />
      )}
    </>
  );
}
